import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Users, Dumbbell, ClipboardList, Settings, Brain,
  ArrowRight, Rocket, CheckCircle2, Circle, Sparkles,
  UserPlus, BookOpen, Zap, ChevronDown, ChevronUp,
  Apple, BarChart3, DollarSign, MessageSquare, TrendingUp,
  ArrowLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

function getTourKey(userId?: string) { return `professor_tour_completed_${userId || "anon"}`; }
function getChecklistKey(userId?: string) { return `professor_onboarding_checklist_${userId || "anon"}`; }
function getChecklistDismissedKey(userId?: string) { return `professor_checklist_dismissed_${userId || "anon"}`; }

// ── Tour Steps ──

interface TourStep {
  title: string;
  description: string;
  icon: React.ElementType;
  accent: string;
  tip?: string;
  action?: { label: string; path: string };
}

const STEPS: TourStep[] = [
  {
    title: "Bem-vindo à sua academia digital! 🎉",
    description: "Vou te guiar pelos principais recursos em menos de 2 minutos. Ao final, você terá um checklist interativo de primeiros passos.",
    icon: Rocket,
    accent: "from-primary/20 to-primary/5",
    tip: "Dica: Você pode pular a qualquer momento e voltar ao checklist depois.",
  },
  {
    title: "Gestão de Alunos",
    description: "Cadastre alunos, crie treinos personalizados com IA, acompanhe evolução e receba alertas automáticos de inatividade ou dor.",
    icon: Users,
    accent: "from-primary/20 to-primary/5",
    tip: "💡 Envie o link de autocadastro e o aluno já chega com treino gerado pela IA.",
    action: { label: "Ver Dashboard", path: "/" },
  },
  {
    title: "Biblioteca de Exercícios",
    description: "+800 exercícios com GIFs animados. Crie exercícios customizados, adicione vídeos da sua metodologia e organize por grupo muscular.",
    icon: Dumbbell,
    accent: "from-amber-500/20 to-amber-500/5",
    tip: "💡 Você pode importar exercícios em lote ou criar os seus próprios.",
    action: { label: "Explorar Biblioteca", path: "/exercicios" },
  },
  {
    title: "Anamneses Inteligentes",
    description: "Envie um link ou QR Code. O aluno preenche, a IA analisa riscos clínicos (PAR-Q, lesões, medicamentos) e gera diretrizes de treino automaticamente.",
    icon: ClipboardList,
    accent: "from-emerald-500/20 to-emerald-500/5",
    tip: "💡 A IA detecta 20+ condições clínicas e adapta a prescrição automaticamente.",
    action: { label: "Ver Anamneses", path: "/admin/anamneses" },
  },
  {
    title: "IA que trabalha por você",
    description: "Geração de treinos, revisão de séries, análise de progressão e chat com alunos. A IA de treino, nutrição e chat compartilham contexto — uma integração que nenhum concorrente tem.",
    icon: Brain,
    accent: "from-purple-500/20 to-purple-500/5",
    tip: "💡 Configure o 'DNA da sua academia' nas Configurações para a IA aprender seu estilo.",
  },
  {
    title: "Controle Financeiro",
    description: "Registre planos, formas de pagamento, vencimentos e receba alertas automáticos de inadimplência. Tudo centralizado no painel financeiro.",
    icon: DollarSign,
    accent: "from-green-500/20 to-green-500/5",
    tip: "💡 Alertas de inadimplência são enviados automaticamente. Você não precisa cobrar ninguém.",
    action: { label: "Ver Financeiro", path: "/financeiro" },
  },
  {
    title: "Personalize sua marca",
    description: "Adicione o logo da sua academia, escolha a cor tema e instale o app no celular. Seus alunos terão um app profissional com a sua identidade visual.",
    icon: Settings,
    accent: "from-blue-500/20 to-blue-500/5",
    tip: "💡 No plano Academy+, o app do aluno exibe 100% a sua marca.",
    action: { label: "Ir para Configurações", path: "/configuracoes" },
  },
];

// ── Checklist Items ──

interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  icon: React.ElementType;
  path: string;
  autoDetect?: boolean;
}

const CHECKLIST_ITEMS: ChecklistItem[] = [
  {
    id: "add_student",
    label: "Cadastrar primeiro aluno",
    description: "Adicione um aluno para começar a criar treinos",
    icon: UserPlus,
    path: "/",
    autoDetect: true,
  },
  {
    id: "explore_exercises",
    label: "Explorar a biblioteca de exercícios",
    description: "Conheça os +800 exercícios disponíveis",
    icon: BookOpen,
    path: "/exercicios",
  },
  {
    id: "generate_workout",
    label: "Gerar primeiro treino com IA",
    description: "Selecione um aluno e peça para a IA criar o treino",
    icon: Brain,
    path: "/",
    autoDetect: true,
  },
  {
    id: "setup_academy",
    label: "Configurar o Centro de Inteligência",
    description: "Calibre a IA com o DNA da sua academia",
    icon: Zap,
    path: "/configuracoes",
  },
  {
    id: "customize_brand",
    label: "Personalizar logo e cor tema",
    description: "Dê a cara da sua academia ao sistema",
    icon: Settings,
    path: "/configuracoes",
  },
  {
    id: "setup_finance",
    label: "Configurar controle financeiro",
    description: "Registre planos e formas de pagamento",
    icon: DollarSign,
    path: "/financeiro",
  },
];

// ── Tour Component ──

function TourModal({ onComplete }: { onComplete: () => void }) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;

  const next = () => {
    if (isLast) onComplete();
    else setStep(step + 1);
  };

  const prev = () => {
    if (!isFirst) setStep(step - 1);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onComplete}
      />
      <motion.div
        key={step}
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="fixed inset-x-4 top-1/2 -translate-y-1/2 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[460px] z-50"
      >
        <div className="glass-card rounded-3xl p-6 border border-border/30 shadow-2xl relative overflow-hidden">
          <div className={`absolute inset-0 bg-gradient-to-br ${current.accent} pointer-events-none`} />

          <div className="relative space-y-5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-primary/60 tracking-[0.3em] uppercase">
                {step + 1} / {STEPS.length}
              </span>
              <button onClick={onComplete} className="text-muted-foreground hover:text-foreground transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 flex items-center justify-center">
              <Icon className="h-7 w-7 text-primary" />
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-bold text-foreground">{current.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{current.description}</p>
            </div>

            {current.tip && (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="px-3 py-2.5 rounded-xl bg-primary/5 border border-primary/10 text-xs text-primary/80 leading-relaxed"
              >
                {current.tip}
              </motion.div>
            )}

            {current.action && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-xl text-xs"
                onClick={() => {
                  onComplete();
                  navigate(current.action!.path);
                }}
              >
                {current.action.label}
                <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            )}

            <div className="flex items-center justify-between pt-2">
              <div className="flex gap-1.5">
                {STEPS.map((_, i) => (
                  <motion.div
                    key={i}
                    className={cn(
                      "h-1.5 rounded-full transition-all duration-300",
                      i === step ? "w-6 bg-primary" : i < step ? "w-1.5 bg-primary/40" : "w-1.5 bg-muted-foreground/20"
                    )}
                    layoutId={`dot-${i}`}
                  />
                ))}
              </div>

              <div className="flex items-center gap-2">
                {!isFirst && (
                  <Button variant="ghost" size="sm" onClick={prev} className="text-xs text-muted-foreground gap-1">
                    <ArrowLeft className="h-3 w-3" />
                    Voltar
                  </Button>
                )}
                {isFirst && (
                  <Button variant="ghost" size="sm" onClick={onComplete} className="text-xs text-muted-foreground">
                    Pular
                  </Button>
                )}
                <Button size="sm" onClick={next} className="gap-1.5">
                  {isLast ? (
                    <>
                      <Sparkles className="h-3.5 w-3.5" />
                      Ver Checklist
                    </>
                  ) : (
                    <>
                      Próximo
                      <ArrowRight className="h-3.5 w-3.5" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
}

// ── Checklist Widget ──

function ChecklistWidget({ userId, tenantId }: { userId?: string; tenantId?: string }) {
  const navigate = useNavigate();
  const checklistKey = useMemo(() => getChecklistKey(userId), [userId]);
  const dismissedKey = useMemo(() => getChecklistDismissedKey(userId), [userId]);
  const [completed, setCompleted] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(checklistKey) || "[]"); }
    catch { return []; }
  });
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(dismissedKey) === "1");
  const [expanded, setExpanded] = useState(true);

  // Auto-detect completed items from DB
  useEffect(() => {
    if (!tenantId) return;
    const detect = async () => {
      const autoCompleted: string[] = [];
      try {
        const { count: studentCount } = await supabase
          .from("students")
          .select("id", { count: "exact", head: true })
          .eq("tenant_id", tenantId);
        if (studentCount && studentCount > 0) autoCompleted.push("add_student");

        const { count: workoutCount } = await supabase
          .from("workouts")
          .select("id", { count: "exact", head: true })
          .eq("tenant_id", tenantId);
        if (workoutCount && workoutCount > 0) autoCompleted.push("generate_workout");
      } catch { /* ignore */ }

      if (autoCompleted.length > 0) {
        setCompleted(prev => {
          const merged = [...new Set([...prev, ...autoCompleted])];
          localStorage.setItem(checklistKey, JSON.stringify(merged));
          return merged;
        });
      }
    };
    detect();
  }, [tenantId, checklistKey]);

  const toggleComplete = useCallback((id: string) => {
    setCompleted(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      localStorage.setItem(checklistKey, JSON.stringify(next));
      return next;
    });
  }, [checklistKey]);

  const progress = completed.length / CHECKLIST_ITEMS.length;
  const allDone = completed.length === CHECKLIST_ITEMS.length;

  if (dismissed) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.96 }}
      transition={{ type: "spring", damping: 25, stiffness: 300 }}
      className="fixed bottom-4 right-4 z-40 w-[340px] max-w-[calc(100vw-2rem)]"
    >
      <div className="glass-card rounded-2xl border border-border/30 shadow-2xl overflow-hidden">
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between p-4 pb-3 text-left hover:bg-muted/5 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <div className="relative h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center">
              {allDone ? (
                <CheckCircle2 className="h-4.5 w-4.5 text-primary" />
              ) : (
                <Rocket className="h-4.5 w-4.5 text-primary" />
              )}
              {!allDone && (
                <motion.div
                  className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-primary flex items-center justify-center"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <span className="text-[7px] font-bold text-primary-foreground">{CHECKLIST_ITEMS.length - completed.length}</span>
                </motion.div>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground">
                {allDone ? "Tudo pronto! 🎉" : "Primeiros Passos"}
              </p>
              <p className="text-[10px] text-muted-foreground">
                {completed.length}/{CHECKLIST_ITEMS.length} concluídos
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDismissed(true);
                localStorage.setItem(dismissedKey, "1");
              }}
              className="h-5 w-5 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </div>
        </button>

        {/* Progress bar */}
        <div className="px-4 pb-2">
          <div className="h-1.5 rounded-full bg-muted/30 overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-primary to-primary/70"
              initial={{ width: 0 }}
              animate={{ width: `${progress * 100}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>
        </div>

        {/* Items */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-3 pb-3 space-y-1">
                {CHECKLIST_ITEMS.map((item, idx) => {
                  const done = completed.includes(item.id);
                  const ItemIcon = item.icon;
                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className={cn(
                        "flex items-center gap-2.5 p-2.5 rounded-xl transition-all cursor-pointer group",
                        done ? "bg-primary/5" : "hover:bg-muted/20"
                      )}
                      onClick={() => {
                        if (!done) navigate(item.path);
                      }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleComplete(item.id);
                        }}
                        className="shrink-0"
                      >
                        {done ? (
                          <CheckCircle2 className="h-4.5 w-4.5 text-primary" />
                        ) : (
                          <Circle className="h-4.5 w-4.5 text-muted-foreground/40 group-hover:text-primary/50 transition-colors" />
                        )}
                      </button>
                      <div className="min-w-0 flex-1">
                        <p className={cn(
                          "text-xs font-medium transition-colors",
                          done ? "text-muted-foreground line-through" : "text-foreground"
                        )}>
                          {item.label}
                        </p>
                        {!done && (
                          <p className="text-[10px] text-muted-foreground mt-0.5 truncate">
                            {item.description}
                          </p>
                        )}
                      </div>
                      {!done && (
                        <ArrowRight className="h-3 w-3 text-muted-foreground/30 group-hover:text-primary/50 shrink-0 transition-colors" />
                      )}
                    </motion.div>
                  );
                })}
              </div>

              {allDone && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="px-3 pb-3 space-y-2"
                >
                  <div className="px-3 py-2 rounded-xl bg-primary/5 border border-primary/10 text-center">
                    <p className="text-xs text-primary font-medium">🚀 Sua academia está configurada!</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Agora é só cadastrar alunos e deixar a IA trabalhar por você.</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full rounded-xl text-xs"
                    onClick={() => {
                      setDismissed(true);
                      localStorage.setItem(dismissedKey, "1");
                    }}
                  >
                    Fechar checklist
                  </Button>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// ── Main Export ──

import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/hooks/useTenant";

export default function ProfessorOnboardingTour() {
  const { user } = useAuth();
  const { tenantId } = useTenant();
  const [showTour, setShowTour] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);

  const tourKey = useMemo(() => getTourKey(user?.id), [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const tourDone = localStorage.getItem(tourKey);
    if (!tourDone) {
      const t = setTimeout(() => setShowTour(true), 1200);
      return () => clearTimeout(t);
    } else {
      setShowChecklist(true);
    }
  }, [user?.id, tourKey]);

  const handleTourComplete = () => {
    setShowTour(false);
    localStorage.setItem(tourKey, "1");
    setShowChecklist(true);
  };

  return (
    <AnimatePresence>
      {showTour && <TourModal onComplete={handleTourComplete} />}
      {showChecklist && <ChecklistWidget userId={user?.id} tenantId={tenantId} />}
    </AnimatePresence>
  );
}
